import React, { Dispatch, SetStateAction, useState } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MUITable } from 'components/shared/MUITable';
import { OpportunitiesTableColumn, OpportunityListTableEntity } from 'modules/vendorOpportunity/list/types';
import { APIRequestPagination, APIRequestSort, Nullable } from 'global';
import { useOpportunitiesListData } from 'modules/vendorOpportunity/list/hooks/useOpportunitiesListData';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { VOFilters } from 'modules/vendorOpportunity';
import { FormInput } from 'components/form';
import { theme } from 'theme';

interface Props {
  columns: OpportunitiesTableColumn[];
  currentFilterConfig: Nullable<Partial<VOFilters>>;
  searchValue: string;
  setSearch: Dispatch<SetStateAction<string>>;
}

export const OpportunitiesTable = ({ columns, currentFilterConfig, searchValue, setSearch }: Props) => {
  const classes = useStyles();
  const history = useHistory();

  const account = useSelector((state: any) => state.account);

  const [sorting, setSorting] = useState<APIRequestSort>({ key: 'createdOn', value: 'desc' });
  const [pagination, setPagination] = useState<APIRequestPagination>({ page: 1, perPage: 25 });

  const { isLoading, opportunityList, total } = useOpportunitiesListData(
    currentFilterConfig,
    searchValue,
    pagination,
    sorting
  );

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: OpportunityListTableEntity) => {
    if (e.defaultPrevented) return;

    if (
      account.accountType === 'Partner' &&
      ![row.vendorProfileId, row.sourceVendorProfileId].includes(account.vendorProfileId)
    ) {
      return;
    }

    const isNeedOpenVoDetailsInNewTab = e.ctrlKey;
    const voDetailsPath = `/vo/${row.id}`;

    if (isNeedOpenVoDetailsInNewTab) {
      window.open(voDetailsPath, '_blank');
    } else {
      history.push(voDetailsPath);
    }
  };

  return (
    <Box className={classes.root}>
      <FormInput
        label="Search"
        variant="outlined"
        value={searchValue}
        sx={{ maxWidth: '50%' }}
        onChange={value => setSearch(value)}
      />
      <MUITable<OpportunityListTableEntity>
        columns={columns}
        isLoading={isLoading}
        tableData={opportunityList}
        onRowClick={handleRowClick}
        hover={true}
        stickyHeader={true}
        pagination={{
          ...pagination,
          total,
          onChange: nextValue => setPagination(prev => ({ ...prev, ...nextValue })),
        }}
        sorting={{ ...sorting, onChange: setSorting }}
      />
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    gap: '1rem',
    display: 'flex',
    flexDirection: 'column',

    '& .tableContainer': {
      height: 'calc(100vh - 270px)',
      '& .tableHead': {
        whiteSpace: 'nowrap',
      },
      '& .tableRow': {
        cursor: 'pointer',

        '&:nth-of-type(odd)': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
  },
});
